import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { FinancialNote, FinancialNoteType } from '../types';
import { CheckCircle2, Edit3, NotebookPen, Plus, Save, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';

const NOTE_TYPES: Array<{ id: FinancialNoteType; label: string; tone: string }> = [
  { id: 'profit', label: 'Profit', tone: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  { id: 'loss', label: 'Loss', tone: 'bg-red-50 text-red-700 border-red-100' },
  { id: 'debt', label: 'Debt I Owe', tone: 'bg-orange-50 text-orange-700 border-orange-100' },
  { id: 'receivable', label: 'To Receive', tone: 'bg-blue-50 text-blue-700 border-blue-100' }
];

const emptyForm = {
  title: '',
  content: '',
  amount: '',
  type: 'receivable' as FinancialNoteType
};

const Notes: React.FC = () => {
  const [notes, setNotes] = useState<FinancialNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('financial_notes')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      setMessage('Unable to load notes. Please apply the notes table migration.');
      setNotes([]);
    } else {
      setNotes((data || []) as FinancialNote[]);
    }
    setLoading(false);
  };

  const summary = useMemo(() => {
    return notes.reduce((acc, note) => {
      const amount = Number(note.amount) || 0;
      if (note.type === 'profit') acc.profit += amount;
      if (note.type === 'loss') acc.loss += amount;
      if (note.type === 'debt') acc.debt += amount;
      if (note.type === 'receivable') acc.receivable += amount;
      return acc;
    }, { profit: 0, loss: 0, debt: 0, receivable: 0 });
  }, [notes]);

  const netAmount = summary.profit + summary.receivable - summary.loss - summary.debt;
  const totalPositive = summary.profit + summary.receivable;
  const totalNegative = summary.loss + summary.debt;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.amount) return;

    setSaving(true);
    setMessage('');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setSaving(false);
      return;
    }

    const payload = {
      user_id: user.id,
      title: formData.title.trim(),
      content: formData.content.trim() || null,
      amount: Number(formData.amount),
      type: formData.type,
      updated_at: new Date().toISOString()
    };

    const request = editingId
      ? supabase.from('financial_notes').update(payload).eq('id', editingId).eq('user_id', user.id)
      : supabase.from('financial_notes').insert([payload]);

    const { error } = await request;
    if (error) {
      setMessage(error.message);
    } else {
      setMessage(editingId ? 'Note updated' : 'Note saved');
      resetForm();
      await fetchNotes();
    }
    setSaving(false);
  };

  const startEdit = (note: FinancialNote) => {
    setEditingId(note.id);
    setFormData({
      title: note.title,
      content: note.content || '',
      amount: String(note.amount),
      type: note.type
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteNote = async (noteId: string) => {
    if (!window.confirm('Delete this note?')) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('financial_notes')
      .delete()
      .eq('id', noteId)
      .eq('user_id', user.id);

    if (error) setMessage(error.message);
    else {
      setMessage('Note deleted');
      await fetchNotes();
    }
  };

  if (loading) {
    return <div className="pt-20 flex justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-8 pb-20">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Notes</h1>
          <p className="text-slate-400 text-sm font-medium">Write money notes and track your total position</p>
        </div>
        <div className="w-12 h-12 rounded-2xl bg-[#0f172a] text-[#fbbf24] flex items-center justify-center shadow-xl">
          <NotebookPen size={22} />
        </div>
      </header>

      {message && (
        <div className="bg-white border border-slate-100 text-slate-700 p-5 rounded-[2rem] shadow-sm flex items-center gap-3">
          <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
          <span className="text-[10px] font-black uppercase tracking-widest leading-relaxed">{message}</span>
        </div>
      )}

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`${netAmount >= 0 ? 'bg-[#0f172a] text-white' : 'bg-red-600 text-white'} p-8 rounded-[3rem] shadow-2xl`}>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-70">Net Result</p>
          <p className="text-4xl font-black mt-3 break-words">{formatCurrency(Math.abs(netAmount))}</p>
          <p className="text-[10px] font-black uppercase tracking-widest mt-4 text-[#fbbf24]">{netAmount >= 0 ? 'Profit Position' : 'Loss Position'}</p>
        </div>

        <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-xl shadow-slate-200/50">
          <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em]">Positive Notes</p>
          <p className="text-3xl font-black text-emerald-700 mt-3">{formatCurrency(totalPositive)}</p>
          <p className="text-[10px] font-bold text-slate-400 mt-4">Profit + To Receive</p>
        </div>

        <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-xl shadow-slate-200/50">
          <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em]">Negative Notes</p>
          <p className="text-3xl font-black text-red-600 mt-3">{formatCurrency(totalNegative)}</p>
          <p className="text-[10px] font-bold text-slate-400 mt-4">Loss + Debt I Owe</p>
        </div>
      </section>

      <form onSubmit={handleSubmit} className="bg-white p-8 md:p-10 rounded-[3rem] border border-slate-100 shadow-xl shadow-slate-200/50 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">{editingId ? 'Edit Note' : 'New Note'}</h2>
          {editingId && (
            <button type="button" onClick={resetForm} className="p-3 rounded-2xl bg-slate-50 text-slate-400 hover:text-slate-900 transition-colors">
              <X size={18} />
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Title</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-[1.8rem] outline-none font-bold text-slate-900"
              placeholder="Example: Friend has to return money"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Amount</label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-[1.8rem] outline-none font-black text-slate-900"
              placeholder="0"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Note Type</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {NOTE_TYPES.map((noteType) => (
              <button
                key={noteType.id}
                type="button"
                onClick={() => setFormData({ ...formData, type: noteType.id })}
                className={`py-4 px-3 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                  formData.type === noteType.id ? 'bg-[#0f172a] text-[#fbbf24] border-[#0f172a] shadow-lg' : noteType.tone
                }`}
              >
                {noteType.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 ml-1">Details</label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-[1.8rem] outline-none font-medium text-slate-700 min-h-[130px]"
            placeholder="Write anything you want to remember about this money note..."
          />
        </div>

        <button type="submit" disabled={saving} className="w-full bg-[#0f172a] text-[#fbbf24] py-5 rounded-[2.2rem] font-black shadow-2xl flex items-center justify-center disabled:opacity-50 active:scale-95 transition-all">
          {saving ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <><Save size={18} className="mr-3" /> {editingId ? 'Update Note' : 'Save Note'}</>}
        </button>
      </form>

      <section className="space-y-4">
        <div className="flex items-center justify-between px-2">
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest">Saved Notes</h2>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{notes.length} Total</span>
        </div>

        {notes.length === 0 ? (
          <div className="bg-white rounded-[3rem] border border-dashed border-slate-200 p-12 text-center">
            <Plus size={36} className="mx-auto text-slate-200 mb-4" />
            <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No notes yet</p>
          </div>
        ) : (
          notes.map((note) => {
            const noteType = NOTE_TYPES.find((item) => item.id === note.type) || NOTE_TYPES[0];
            const isPositive = note.type === 'profit' || note.type === 'receivable';
            return (
              <div key={note.id} className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span className={`px-3 py-1.5 rounded-xl border text-[8px] font-black uppercase tracking-widest ${noteType.tone}`}>{noteType.label}</span>
                      <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest">{format(new Date(note.created_at), 'dd MMM yyyy')}</span>
                    </div>
                    <h3 className="font-black text-slate-900 text-lg leading-tight">{note.title}</h3>
                    {note.content && <p className="text-xs text-slate-500 font-medium leading-relaxed mt-3 whitespace-pre-wrap">{note.content}</p>}
                  </div>

                  <div className="flex md:flex-col items-center md:items-end justify-between gap-4">
                    <p className={`text-xl font-black ${isPositive ? 'text-emerald-700' : 'text-red-600'}`}>
                      {isPositive ? '+' : '-'}{formatCurrency(Number(note.amount))}
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(note)} className="p-3 rounded-2xl bg-slate-50 text-slate-400 hover:text-slate-900 transition-colors">
                        <Edit3 size={17} />
                      </button>
                      <button onClick={() => deleteNote(note.id)} className="p-3 rounded-2xl bg-red-50 text-red-500 hover:bg-red-100 transition-colors">
                        <Trash2 size={17} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
};

export default Notes;
